import { useEffect, useMemo, useReducer, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  dashboardFilterReducer,
  initialDashboardFilterState,
  type DashboardFilterAction,
  type DashboardFilterState,
} from './dashboardFilterReducer'

/**
 * URL-addressable dashboard state (AC14/15/16). **Criteria only — never a
 * cached data payload** (AC15): the URL carries just enough to reconstruct
 * the *request*, so opening a shared link always re-queries the backend as
 * the recipient (their own capability/RLS-scoped rows), never a snapshot of
 * the sender's. A stable, versioned key scheme (`v`, `f.*`, `x.*`, `s.*`,
 * `t`, `asOfFrom`, `asOfTo`) keeps this pure-function-testable independent
 * of the router.
 */
export const DASHBOARD_URL_STATE_VERSION = 1

export interface ParsedDashboardUrlState {
  state: DashboardFilterState
  /** Any query key that didn't match the known scheme (AC16 — ignored, not fatal). */
  unknownKeys: string[]
}

export function serializeDashboardState(state: DashboardFilterState): URLSearchParams {
  const params = new URLSearchParams()
  params.set('v', String(DASHBOARD_URL_STATE_VERSION))
  for (const [axis, value] of Object.entries(state.filters)) {
    if (value) params.set(`f.${axis}`, value)
  }
  for (const [axis, value] of Object.entries(state.crossFilters)) {
    if (value) params.set(`x.${axis}`, value)
  }
  for (const [axis, range] of Object.entries(state.sliders)) {
    params.set(`s.${axis}`, `${range[0]},${range[1]}`)
  }
  if (state.territory) params.set('t', state.territory)
  if (state.asOf?.from) params.set('asOfFrom', state.asOf.from)
  if (state.asOf?.to) params.set('asOfTo', state.asOf.to)
  // crossFilterOrigin is transient UI state (which widget last cross-filtered,
  // used only to skip that widget's own re-fetch) — never URL-addressable,
  // it is not filter criteria.
  return params
}

export function parseDashboardState(params: URLSearchParams): ParsedDashboardUrlState {
  const state: DashboardFilterState = { filters: {}, crossFilters: {}, sliders: {} }
  const unknownKeys: string[] = []

  for (const [key, value] of params.entries()) {
    if (key === 'v') continue
    if (key === 't') {
      state.territory = value
      continue
    }
    if (key === 'asOfFrom') {
      state.asOf = { ...state.asOf, from: value }
      continue
    }
    if (key === 'asOfTo') {
      state.asOf = { ...state.asOf, to: value }
      continue
    }
    if (key.startsWith('f.')) {
      state.filters[key.slice(2) as keyof DashboardFilterState['filters']] = value
      continue
    }
    if (key.startsWith('x.')) {
      state.crossFilters[key.slice(2) as keyof DashboardFilterState['crossFilters']] = value
      continue
    }
    if (key.startsWith('s.')) {
      const [loStr, hiStr] = value.split(',')
      const lo = Number(loStr)
      const hi = Number(hiStr)
      if (Number.isFinite(lo) && Number.isFinite(hi)) {
        state.sliders[key.slice(2)] = [lo, hi]
        continue
      }
    }
    unknownKeys.push(key)
  }

  return { state, unknownKeys }
}

export interface UseDashboardUrlStateResult {
  state: DashboardFilterState
  dispatch: (action: DashboardFilterAction) => void
  /** AC16 — surfaced as a dismissible notice, never a 500/blank dashboard. */
  unknownKeys: string[]
  noticeDismissed: boolean
  dismissNotice: () => void
}

/** Hydrates from the URL on mount, keeps the URL in sync as the reducer changes (`replace`, never pushes history per click). */
export function useDashboardUrlState(): UseDashboardUrlStateResult {
  const [searchParams, setSearchParams] = useSearchParams()
  // Parsed once at mount — this hook is only ever re-mounted by a real
  // navigation (route change / new tab), which is exactly when a fresh
  // hydrate-from-URL should happen (AC14's "fresh context" case).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => parseDashboardState(searchParams), [])
  const [state, dispatch] = useReducer(dashboardFilterReducer, initial.state)
  const [unknownKeys] = useState(initial.unknownKeys)
  const [noticeDismissed, setNoticeDismissed] = useState(false)

  useEffect(() => {
    setSearchParams(serializeDashboardState(state), { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  return {
    state,
    dispatch,
    unknownKeys,
    noticeDismissed,
    dismissNotice: () => setNoticeDismissed(true),
  }
}

export { initialDashboardFilterState }
